import { basicSort, COMMON_TABLE_COLUMN_WIDTHS, geneLinkSort, numericSort, TableCellInteger, TableCellLink, TableCellPercent, TableCellPValue, TableCellScientificNumeric, variantLinkSort, } from 'Shared/components/Table';
import { ELevelResult, ETraitsTypes } from 'Shared/types';
import createTableVariantLink from 'Common/components/Table/factories/createTableVariantLink/createTableVariantLink';
import createTableGeneLink from 'Common/components/Table/factories/createTableGeneLink/createTableGeneLink';
import styles from 'Shared/components/Table/components/TableView/TableView.css';
export var ETableColumnAccessor;
(function (ETableColumnAccessor) {
    ETableColumnAccessor["Variant"] = "variant";
    ETableColumnAccessor["VariantName"] = "variant.name";
    ETableColumnAccessor["VariantType"] = "variant.type";
    ETableColumnAccessor["VariantId"] = "variant.id";
    ETableColumnAccessor["Variants"] = "variants";
    ETableColumnAccessor["ConsequenceType"] = "consequenceType";
    ETableColumnAccessor["GeneName"] = "gene.name";
    ETableColumnAccessor["Ancestry"] = "ancestry";
    ETableColumnAccessor["CollapsingModelName"] = "collapsingModel.name";
    ETableColumnAccessor["TranscriptId"] = "transcriptId";
    ETableColumnAccessor["CDNAChange"] = "CDNAChange";
    ETableColumnAccessor["AAChange"] = "AAChange";
    ETableColumnAccessor["ExonRank"] = "exonRank";
    ETableColumnAccessor["Cases"] = "cases";
    ETableColumnAccessor["AACases"] = "AACases";
    ETableColumnAccessor["ABCases"] = "ABCases";
    ETableColumnAccessor["BBCases"] = "BBCases";
    ETableColumnAccessor["CaseMAF"] = "caseMAF";
    ETableColumnAccessor["CaseAAF"] = "caseAAF";
    ETableColumnAccessor["ABorBBCasesPercentage"] = "ABOrBBCasesPercentage";
    ETableColumnAccessor["BBCasesPercentage"] = "BBCasesPercentage";
    ETableColumnAccessor["Controls"] = "controls";
    ETableColumnAccessor["AAControls"] = "AAControls";
    ETableColumnAccessor["ABControls"] = "ABControls";
    ETableColumnAccessor["BBControls"] = "BBControls";
    ETableColumnAccessor["ControlMAF"] = "controlMaf";
    ETableColumnAccessor["ControlAAF"] = "controlAaf";
    ETableColumnAccessor["ABorBBControlsPercentage"] = "ABOrBBControlsPercentage";
    ETableColumnAccessor["BBControlsPercentage"] = "BBControlsPercentage";
    ETableColumnAccessor["PValue"] = "pvalue";
    ETableColumnAccessor["OddsRatio"] = "oddsRatio";
    ETableColumnAccessor["OddsRatioLCI"] = "oddsRatioLCI";
    ETableColumnAccessor["OddsRatioUCI"] = "oddsRatioUCI";
})(ETableColumnAccessor || (ETableColumnAccessor = {}));
export const TABLE_COLUMNS_HEADERS = {
    [ETableColumnAccessor.Variant]: 'Variant',
    [ETableColumnAccessor.VariantType]: 'Variant type',
    [ETableColumnAccessor.PValue]: 'p-value',
    [ETableColumnAccessor.CollapsingModelName]: 'Model',
    [ETableColumnAccessor.ConsequenceType]: 'Consequence type',
    [ETableColumnAccessor.GeneName]: 'Gene',
    [ETableColumnAccessor.Ancestry]: 'Ancestry',
    [ETableColumnAccessor.TranscriptId]: 'Transcript',
    [ETableColumnAccessor.CDNAChange]: 'cDNA change',
    [ETableColumnAccessor.AAChange]: 'Amino acid change',
    [ETableColumnAccessor.ExonRank]: 'Exon rank',
    [ETableColumnAccessor.Cases]: 'No. cases',
    [ETableColumnAccessor.AACases]: 'No. AA cases',
    [ETableColumnAccessor.ABCases]: 'No. AB cases',
    [ETableColumnAccessor.BBCases]: 'No. BB cases',
    [ETableColumnAccessor.CaseAAF]: 'Case AAF',
    [ETableColumnAccessor.ABorBBCasesPercentage]: '% AB or BB cases',
    [ETableColumnAccessor.BBCasesPercentage]: '% BB cases',
    [ETableColumnAccessor.Controls]: 'No. controls',
    [ETableColumnAccessor.AAControls]: 'No. AA controls',
    [ETableColumnAccessor.ABControls]: 'No. AB controls',
    [ETableColumnAccessor.BBControls]: 'No. BB controls',
    [ETableColumnAccessor.ControlAAF]: 'Control AAF',
    [ETableColumnAccessor.ABorBBControlsPercentage]: '% AB or BB controls',
    [ETableColumnAccessor.BBControlsPercentage]: '% BB controls',
    [ETableColumnAccessor.OddsRatio]: 'Odds ratio',
    [ETableColumnAccessor.OddsRatioLCI]: 'Odds ratio LCI',
    [ETableColumnAccessor.OddsRatioUCI]: 'Odds ratio UCI',
};
/**
 * Get table columns
 *
 * @param descriptions - Text descriptions for the columns
 * @param referrerData - Referrer data for the variant link
 * @param columnsData - Columns data for the variant link
 * @param hasAncestryData - Boolean value which determines if ancestry data exists
 * @returns formatted table columns
 */
export const getTableColumns = (descriptions, referrerData, columnsData, hasAncestryData) => [
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.Variant],
        accessor: ETableColumnAccessor.Variant,
        description: descriptions[ETableColumnAccessor.Variant],
        className: styles.alignLeft,
        Cell: TableCellLink({
            additionalData: columnsData,
            traitsType: ETraitsTypes.Binary,
            referrerData,
        }, createTableVariantLink),
        fixed: true,
        width: COMMON_TABLE_COLUMN_WIDTHS.VARIANT,
        exported: {
            Header: 'Variant',
            accessor: ETableColumnAccessor.VariantName,
        },
        sortType: variantLinkSort,
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.VariantType],
        accessor: ETableColumnAccessor.VariantType,
        description: descriptions[ETableColumnAccessor.VariantType],
        className: styles.alignLeft,
        width: COMMON_TABLE_COLUMN_WIDTHS.VARIANT_TYPE,
        sortType: basicSort,
    },
    ...(hasAncestryData ? [
        {
            Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.Ancestry],
            accessor: ETableColumnAccessor.Ancestry,
            description: descriptions[ETableColumnAccessor.Ancestry],
            width: COMMON_TABLE_COLUMN_WIDTHS.ANCESTRY,
            sortType: basicSort,
        },
    ] : []),
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.PValue],
        accessor: ETableColumnAccessor.PValue,
        description: descriptions[ETableColumnAccessor.PValue],
        Cell: TableCellPValue,
        sortType: numericSort,
        className: styles.alignLeft,
        width: COMMON_TABLE_COLUMN_WIDTHS.P_VALUE,
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.CollapsingModelName],
        accessor: ETableColumnAccessor.CollapsingModelName,
        description: descriptions[ETableColumnAccessor.CollapsingModelName],
        className: styles.alignLeft,
        width: COMMON_TABLE_COLUMN_WIDTHS.MODEL,
        sortType: basicSort,
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.ConsequenceType],
        accessor: ETableColumnAccessor.ConsequenceType,
        description: descriptions[ETableColumnAccessor.ConsequenceType],
        className: styles.alignLeft,
        width: COMMON_TABLE_COLUMN_WIDTHS.CONSEQUENCE_TYPE,
        sortType: basicSort,
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.GeneName],
        accessor: ETableColumnAccessor.GeneName,
        description: descriptions[ETableColumnAccessor.GeneName],
        className: styles.alignLeft,
        Cell: TableCellLink({
            levelResult: ELevelResult.Variant,
            traitsType: ETraitsTypes.Binary,
            referrerData,
        }, createTableGeneLink),
        width: COMMON_TABLE_COLUMN_WIDTHS.GENE,
        sortType: geneLinkSort,
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.TranscriptId],
        accessor: ETableColumnAccessor.TranscriptId,
        description: descriptions[ETableColumnAccessor.TranscriptId],
        className: styles.alignLeft,
        width: COMMON_TABLE_COLUMN_WIDTHS.TRANSCRIPT,
        sortType: basicSort,
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.CDNAChange],
        accessor: ETableColumnAccessor.CDNAChange,
        description: descriptions[ETableColumnAccessor.CDNAChange],
        className: styles.alignLeft,
        width: COMMON_TABLE_COLUMN_WIDTHS.CDNA_CHANGE,
        sortType: basicSort,
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.AAChange],
        accessor: ETableColumnAccessor.AAChange,
        description: descriptions[ETableColumnAccessor.AAChange],
        className: styles.alignLeft,
        width: COMMON_TABLE_COLUMN_WIDTHS.AMINO_ACID_CHANGE,
        sortType: basicSort,
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.ExonRank],
        accessor: ETableColumnAccessor.ExonRank,
        description: descriptions[ETableColumnAccessor.ExonRank],
        className: styles.alignLeft,
        width: COMMON_TABLE_COLUMN_WIDTHS.EXON_RANK,
        sortType: basicSort,
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.Cases],
        accessor: ETableColumnAccessor.Cases,
        description: descriptions[ETableColumnAccessor.Cases],
        Cell: TableCellInteger,
        sortType: numericSort,
        className: styles.alignRight,
        width: COMMON_TABLE_COLUMN_WIDTHS.NUMBER_OF_CASES,
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.AACases],
        accessor: ETableColumnAccessor.AACases,
        description: descriptions[ETableColumnAccessor.AACases],
        Cell: TableCellInteger,
        sortType: numericSort,
        className: styles.alignRight,
        width: COMMON_TABLE_COLUMN_WIDTHS.NUMBER_OF_AA_CASES,
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.ABCases],
        accessor: ETableColumnAccessor.ABCases,
        description: descriptions[ETableColumnAccessor.ABCases],
        Cell: TableCellInteger,
        sortType: numericSort,
        className: styles.alignRight,
        width: COMMON_TABLE_COLUMN_WIDTHS.NUMBER_OF_AB_CASES,
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.BBCases],
        accessor: ETableColumnAccessor.BBCases,
        description: descriptions[ETableColumnAccessor.BBCases],
        Cell: TableCellInteger,
        sortType: numericSort,
        className: styles.alignRight,
        width: COMMON_TABLE_COLUMN_WIDTHS.NUMBER_OF_BB_CASES,
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.CaseAAF],
        accessor: ETableColumnAccessor.CaseAAF,
        description: descriptions[ETableColumnAccessor.CaseAAF],
        Cell: TableCellPercent,
        sortType: numericSort,
        className: styles.alignRight,
        width: COMMON_TABLE_COLUMN_WIDTHS.CASE_AAF,
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.ABorBBCasesPercentage],
        accessor: ETableColumnAccessor.ABorBBCasesPercentage,
        description: descriptions[ETableColumnAccessor.ABorBBCasesPercentage],
        Cell: TableCellPercent,
        sortType: numericSort,
        className: styles.alignRight,
        exported: {
            Header: 'Proportion AB or BB cases',
            accessor: ETableColumnAccessor.ABorBBCasesPercentage,
        },
        width: COMMON_TABLE_COLUMN_WIDTHS.PERCENT_AB_OR_BB_CASES,
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.BBCasesPercentage],
        accessor: ETableColumnAccessor.BBCasesPercentage,
        description: descriptions[ETableColumnAccessor.BBCasesPercentage],
        Cell: TableCellPercent,
        sortType: numericSort,
        className: styles.alignRight,
        exported: {
            Header: 'Proportion BB cases',
            accessor: ETableColumnAccessor.BBCasesPercentage,
        },
        width: COMMON_TABLE_COLUMN_WIDTHS.PERCENT_BB_CASES,
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.Controls],
        accessor: ETableColumnAccessor.Controls,
        description: descriptions[ETableColumnAccessor.Controls],
        Cell: TableCellInteger,
        sortType: numericSort,
        className: styles.alignRight,
        width: COMMON_TABLE_COLUMN_WIDTHS.NUMBER_OF_CONTROLS,
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.AAControls],
        accessor: ETableColumnAccessor.AAControls,
        description: descriptions[ETableColumnAccessor.AAControls],
        Cell: TableCellInteger,
        sortType: numericSort,
        className: styles.alignRight,
        width: COMMON_TABLE_COLUMN_WIDTHS.NUMBER_OF_AA_CONTROLS,
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.ABControls],
        accessor: ETableColumnAccessor.ABControls,
        description: descriptions[ETableColumnAccessor.ABControls],
        Cell: TableCellInteger,
        sortType: numericSort,
        className: styles.alignRight,
        width: COMMON_TABLE_COLUMN_WIDTHS.NUMBER_OF_AB_CONTROLS,
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.BBControls],
        accessor: ETableColumnAccessor.BBControls,
        description: descriptions[ETableColumnAccessor.BBControls],
        Cell: TableCellInteger,
        sortType: numericSort,
        className: styles.alignRight,
        width: COMMON_TABLE_COLUMN_WIDTHS.NUMBER_OF_BB_CONTROLS,
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.ControlAAF],
        accessor: ETableColumnAccessor.ControlAAF,
        description: descriptions[ETableColumnAccessor.ControlAAF],
        Cell: TableCellPercent,
        sortType: numericSort,
        className: styles.alignRight,
        width: COMMON_TABLE_COLUMN_WIDTHS.CONTROL_AAF,
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.ABorBBControlsPercentage],
        accessor: ETableColumnAccessor.ABorBBControlsPercentage,
        description: descriptions[ETableColumnAccessor.ABorBBControlsPercentage],
        Cell: TableCellPercent,
        sortType: numericSort,
        className: styles.alignRight,
        exported: {
            Header: 'Proportion AB or BB controls',
            accessor: ETableColumnAccessor.ABorBBControlsPercentage,
        },
        width: COMMON_TABLE_COLUMN_WIDTHS.PERCENT_AB_OR_BB_CONTROLS,
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.BBControlsPercentage],
        accessor: ETableColumnAccessor.BBControlsPercentage,
        description: descriptions[ETableColumnAccessor.BBControlsPercentage],
        Cell: TableCellPercent,
        sortType: numericSort,
        className: styles.alignRight,
        exported: {
            Header: 'Proportion BB controls',
            accessor: ETableColumnAccessor.BBControlsPercentage,
        },
        width: COMMON_TABLE_COLUMN_WIDTHS.PERCENT_BB_CONTROLS,
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.OddsRatio],
        accessor: ETableColumnAccessor.OddsRatio,
        description: descriptions[ETableColumnAccessor.OddsRatio],
        Cell: TableCellScientificNumeric,
        sortType: numericSort,
        className: styles.alignRight,
        width: COMMON_TABLE_COLUMN_WIDTHS.ODDS_RATIO,
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.OddsRatioLCI],
        accessor: ETableColumnAccessor.OddsRatioLCI,
        description: descriptions[ETableColumnAccessor.OddsRatioLCI],
        Cell: TableCellScientificNumeric,
        sortType: numericSort,
        className: styles.alignRight,
        width: COMMON_TABLE_COLUMN_WIDTHS.ODDS_RATIO_LCI,
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.OddsRatioUCI],
        accessor: ETableColumnAccessor.OddsRatioUCI,
        description: descriptions[ETableColumnAccessor.OddsRatioUCI],
        Cell: TableCellScientificNumeric,
        sortType: numericSort,
        className: styles.alignRight,
        width: COMMON_TABLE_COLUMN_WIDTHS.ODDS_RATIO_UCI,
    },
];
